/**
 * Inactivity Monitor
 * Monitors user inactivity and triggers logout with warning
 */

import { isUserInactive, getTimeUntilInactive, resetActivity, getInactivityTimeout } from './activityTracker';

const CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

let monitorInterval = null;
let logoutCallback = null;
let warningCallback = null;
let warningShown = false;

/**
 * Start monitoring for inactivity
 * @param {Function} onLogout - Callback to execute when user should be logged out
 * @param {Function} onWarning - Callback to show warning dialog (receives seconds remaining)
 */
export const startInactivityMonitor = (onLogout, onWarning) => {
  // Stop any existing monitor
  stopInactivityMonitor();

  logoutCallback = onLogout;
  warningCallback = onWarning;
  warningShown = false;

  // Check immediately
  checkInactivity();

  // Then check periodically
  monitorInterval = setInterval(() => {
    checkInactivity();
  }, CHECK_INTERVAL_MS);
};

/**
 * Stop monitoring for inactivity
 */
export const stopInactivityMonitor = () => {
  if (monitorInterval) {
    clearInterval(monitorInterval);
    monitorInterval = null;
  }
  logoutCallback = null;
  warningCallback = null;
  warningShown = false;
};

/**
 * Check current inactivity status
 */
const checkInactivity = () => {
  const timeRemaining = getTimeUntilInactive();
  const isInactive = isUserInactive();
  const inactivityTimeout = getInactivityTimeout();
  const warningThreshold = inactivityTimeout - (60 * 1000); // 1 minute before timeout

  // User is inactive for full timeout period - logout
  if (isInactive) {
    if (logoutCallback) {
      logoutCallback();
    }
    stopInactivityMonitor();
    return;
  }

  // User has less than 1 minute remaining - show warning
  if (timeRemaining <= (inactivityTimeout - warningThreshold) && !warningShown) {
    const secondsRemaining = Math.ceil(timeRemaining / 1000);
    warningShown = true;
    if (warningCallback) {
      warningCallback(secondsRemaining);
    }
  }

  // Reset warning flag if user becomes active again
  if (timeRemaining > (inactivityTimeout - warningThreshold) && warningShown) {
    warningShown = false;
  }
};

/**
 * User clicked "Stay Logged In" - reset activity timer
 */
export const extendSession = () => {
  resetActivity();
  warningShown = false;
};

/**
 * Get current inactivity status
 */
export const getInactivityStatus = () => {
  const timeRemaining = getTimeUntilInactive();
  const isInactive = isUserInactive();
  const secondsRemaining = Math.ceil(timeRemaining / 1000);
  const inactivityTimeout = getInactivityTimeout();
  const warningThreshold = inactivityTimeout - (60 * 1000);

  return {
    isInactive,
    timeRemaining,
    secondsRemaining,
    shouldShowWarning: timeRemaining <= (inactivityTimeout - warningThreshold)
  };
};
